import { Component, Injector, ElementRef, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Calendar } from 'primeng/calendar';
import { takeUntil } from 'rxjs/operators';
import {
  BaseResourceFormComponent, Pagamento, LoginContabil, EddyAutoComplete,
  ContaBancaria, GlobalService, DateFormatPipe, FuncaoService
} from 'eddydata-lib';
import { LiquidacaoService, ContaBancariaService, PagamentoService } from 'administrativo-lib';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'lib-pagamento-form',
  templateUrl: './pagamento-form.component.html'
})
export class PagamentoFormComponent extends BaseResourceFormComponent<Pagamento, LoginContabil> {

  /**
   * Declaração de variáveis
   */
  @ViewChild('InputFocus') private inputField: Calendar;

  public especie: string;
  public liquidado: number;
  public saldoPagar = 0;
  public saldoBancario = 0;
  public contaAutoComplete: EddyAutoComplete<ContaBancaria>;
  public contaId: number;
  public empenhoNumero: number;
  public parcela: number;
  public anulacao = false;
  public redirecionar = false;

  /**
   * Construtor com as injeções de dependencias
   */
  constructor(
    private messageService: MessageService,
    protected injector: Injector,
    protected contaService: ContaBancariaService,
    protected liquidacaoService: LiquidacaoService,
    protected globalService: GlobalService,
    protected pagamentoService: PagamentoService) {
    super(new Pagamento(), injector, Pagamento.converteJson, pagamentoService);
  }

  // ========================================================================
  //                        MÉTODOS ABSTRAÍDOS
  // ========================================================================

  protected criarCamposForm(): void {
    this.entidadeForm = this.fb.group({
      id: [null],
      valor_pago: [0, [Validators.required]],
      valor_retido: [0, [Validators.required]],
      valor_liquido: [0, [Validators.required]],
      mes: [null],
      aux: [null],
      data_pagamento: [null, [Validators.required]],
      data_referencia: [null],
      especie: [null],
      documento: [null, [Validators.required]],
      impresso: [false, [Validators.required]],
      historico: [null, [Validators.required, Validators.minLength(2)]],
      anulacao: [false, [Validators.required]],
      conta: [null, [Validators.required]],
      exercicio: [this.login.exercicio, [Validators.required]],
      orgao: [this.login.orgao, [Validators.required]],
      recurso: [null, [Validators.required]],
      aplicacao: [null, [Validators.required]],
      liquidacao: [null, [Validators.required]],
      usuario_cadastro: [this.login.usuario, [Validators.required]]
    });
  }

  protected parametrosExtras(): {} {
    return {
      relations: 'conta,conta.banco,liquidacao,liquidacao.empenho,liquidacao.empenho.favorecido,' +
        'liquidacao.empenho.subelemento,liquidacao.empenho.ficha,liquidacao.empenho.convenio,' +
        'liquidacao.empenho.ficha.acao,liquidacao.empenho.modalidade,exercicio,orgao,liquidacao.empenho.ficha.recurso,' +
        'liquidacao.empenho.ficha.aplicacao,recurso,aplicacao'
    };
  }

  protected afterInit(): void {
    this.carregarAutoCompletes();
    this.inicializarVariavel();
    this.activatedRoute.params.pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (params: any) => {
          if (params.hasOwnProperty('liquidacao')) {
            const parametros = {};
            parametros['id'] = params['liquidacao'];
            parametros['relations'] = 'empenho,empenho.favorecido,empenho.subelemento,empenho.ficha,empenho.convenio,' +
              'empenho.ficha.acao,empenho.modalidade,exercicio,orgao,empenho.ficha.recurso,empenho.ficha.aplicacao';
            this.liquidacaoService.obter(parametros)
              .pipe(takeUntil(this.unsubscribe))
              .subscribe(
                (entidade) => {
                  if (entidade) {                    
                    this.redirecionar = true;
                    this.entidade.liquidacao = entidade;
                    this.empenhoNumero = this.entidade.liquidacao.empenho.numero;
                    this.parcela = this.entidade.liquidacao.parcela;
                    this.entidadeForm.get('liquidacao').setValue(entidade);
                    this.entidadeForm.get('valor_pago').setValue(entidade.valor_liquidado);
                    this.entidadeForm.get('recurso').setValue(entidade.empenho.ficha.recurso);
                    this.entidadeForm.get('aplicacao').setValue(entidade.empenho.ficha.aplicacao);
                    this.entidadeForm.get('historico')
                      .setValue(`PAGTO EMP. ${entidade.empenho.numero} ${entidade.empenho.favorecido.nome}`);
                    this.buscarTotalLiquidado(this.entidade.liquidacao.empenho.numero);
                  } else {
                    this.messageService.add({
                      severity: 'info', summary: 'Informação',
                      detail: 'Empenho não foi encontrado!'
                    });
                  }
                }, () => this.sair());

          }
        });
  }

  protected campoFoco(): ElementRef<any> {
    return this.inputField.inputfieldViewChild.nativeElement.focus();
  }

  protected afterLoad() {
    this.entidade.data_pagamento = new DateFormatPipe().transform(this.entidade.data_pagamento, []);
    this.empenhoNumero = this.entidade.liquidacao.empenho.numero;
    this.parcela = this.entidade.liquidacao.parcela;
    this.contaId = this.entidade.conta.id;
    this.anulacao = this.entidade.anulacao;
    this.buscarSaldoBancario();
    this.buscarTotalLiquidado(this.entidade.liquidacao.empenho.numero);
    this.inicializarVariavel();
  }

  protected afterSubmit(ent: Pagamento) {
    if (this.currentActionRoute === 'novo' && this.login.redirecionar_liquidar_pagar && this.redirecionar
      && this.podeIncluir('empenhos')) {
      this.router.navigate(['/empenhos', 'novo']);
    }
  }

  public sair() {
    if (this.redirecionar && this.podeIncluir('empenhos')) {
      this.router.navigate(['/empenhos']);
    } else {
      new FuncaoService().navegarPara(this.login.usuario.sistema, this.router);
    }
  }

  protected beforeSubmit() {
    try {
      const dataPagamento = new Date(this.entidadeForm.get('data_pagamento').value);
      this.entidadeForm.get('mes').setValue(dataPagamento.getUTCMonth() + 1);
      this.entidadeForm.get('aux').setValue(0);
      this.entidadeForm.get('especie').setValue(!this.anulacao ? 'PGO' : 'PGA');
      this.entidadeForm.get('anulacao').setValue(this.anulacao);

      if (this.anulacao) {
        if (this.entidadeForm.get('valor_pago').value >= 0) {
          throw new Error('Valor anulado deve ser negativo!');
        }
      }
      if (this.entidade.liquidacao.data_liquidacao > dataPagamento) {
        throw new Error('Data de pagamento tem que ser maior ou igual a data de liquidação!');
      }
      if ((+this.saldoBancario - +this.entidadeForm.get('valor_pago').value) <= 0) {
        throw new Error('Saldo de banco insuficiente para efetuar o pagamento.');
      }
      const valor_liquidado = +this.entidadeForm.get('valor_pago').value - +this.entidadeForm.get('valor_retido').value;
      if ((+this.saldoPagar - +(valor_liquidado -
        (this.entidade.valor_pago ? (this.entidade.valor_pago - this.entidade.valor_retido) : 0.0))) < 0) {
        throw new Error('Não há saldo para efeturar o pagto. Verifique se o empenho já está pago ou foi cancelado!');
      }
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Validação', detail: e });
      throw e;
    }
  }

  // ========================================================================
  //                            MÉTODOS DA CLASSE
  // ========================================================================

  private inicializarVariavel() {
    if (this.currentActionRoute === 'novo') {
      this.empenhoNumero = this.entidade.liquidacao ? this.entidade.liquidacao.empenho.numero : null;
      this.parcela = this.entidade.liquidacao ? this.entidade.liquidacao.parcela : null;
      this.entidade.valor_retido = 0;
      this.entidade.impresso = false;
      this.entidade.historico = 'PAGAMENTO EFETUADO NO DIA';
      this.entidade.exercicio = this.login.exercicio;
      this.entidade.orgao = this.login.orgao;
      this.contaId = this.entidade.conta ? this.entidade.conta.id : null;
      this.pagamentoService.ultimaDataPagamento(this.login.exercicio.id, this.login.orgao.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((dataLiq) => {
          if (dataLiq === 0) {
            this.entidade.data_pagamento = new Date();
          } else {
            this.entidade.data_pagamento = new DateFormatPipe().transform(dataLiq, []);
          }
          this.entidadeForm.get('data_pagamento').setValue(this.entidade.data_pagamento);
          this.entidadeForm.get('mes').setValue(this.entidade.data_pagamento.getUTCMonth() + 1);
        });
    }
  }

  public buscarLiquidacao() {
    if (this.empenhoNumero > 0) {
      const param = {};
      param['empenho.numero'] = this.empenhoNumero;
      param['exercicio.id'] = this.login.exercicio.id;
      param['orgao.id'] = this.login.orgao.id;
      param['relations'] = 'empenho,empenho.favorecido,empenho.subelemento,empenho.ficha,empenho.convenio,' +
        'empenho.ficha.acao,exercicio,orgao,empenho.ficha.recurso,empenho.ficha.aplicacao,empenho.modalidade';
      if (this.parcela > 0) {
        param['parcela'] = this.parcela;
      }
      this.liquidacaoService.obter(param).pipe(takeUntil(this.unsubscribe))
        .subscribe(
          (model) => {
            if (model) {
              this.entidade.liquidacao = model;
              this.entidadeForm.get('liquidacao').setValue(model);
              this.entidadeForm.get('recurso').setValue(model.empenho.ficha.recurso);
              this.entidadeForm.get('aplicacao').setValue(model.empenho.ficha.aplicacao);
              this.entidadeForm.get('historico').setValue(`PAGTO EMP. ${model.empenho.numero} ${model.empenho.favorecido.nome}`);
              this.buscarTotalLiquidado(this.entidade.liquidacao.empenho.numero);
            } else {
              this.messageService.add({ severity: 'info', summary: 'Informação', detail: 'Nota de liquidação não foi encontrada!' });
            }
          }, (error) => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error }));
    }
  }

  public buscarConta() {
    if (this.contaId > 0) {
      this.contaService.obterPorFicha(this.contaId, this.login.orgao.id).pipe(takeUntil(this.unsubscribe))
        .subscribe(
          (data: any) => {
            if (data.content) {
              this.entidadeForm.get('conta').setValue(data.content[0]);
              this.buscarSaldoBancario();
            }
          }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error })
        );
    }
  }

  public changeConta() {
    if (this.entidadeForm.get('conta')) {
      const conta = this.entidadeForm.get('conta').value;
      this.contaId = conta.id;
      this.buscarSaldoBancario();
    }
  }

  private carregarAutoCompletes() {
    // autocomplete para conta
    this.contaAutoComplete = new EddyAutoComplete(this.entidadeForm.get('conta'), this.contaService,
      'id', ['numero_conta', 'banco.nome', 'nome'], { orgao_id: this.login.orgao.id, relations: 'banco', orderBy: 'nome' },
      { number: ['numero_conta'], text: ['nome'] },
      this.buscarSaldoBancario()
    );
  }

  public buscarSaldoBancario() {
    if (this.contaId > 0) {
      let datepipe: DatePipe = new DatePipe('pt');
      const dataPagamento = this.entidadeForm.get('data_pagamento').value ? datepipe.transform(this.entidadeForm.get('data_pagamento').value, 'yyyy-MM-dd', 'GMT') :
        datepipe.transform(this.entidade.data_pagamento, 'yyyy-MM-dd', 'GMT');
      this.contaService.obterSaldoBancarioConta(this.contaId, this.login.exercicio.id, this.login.orgao.id, dataPagamento).pipe(takeUntil(this.unsubscribe))
        .subscribe(
          (saldoTotal: any) => {
            this.saldoBancario = saldoTotal;
          }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error })
        );
    }
  }

  public buscarTotalLiquidado(numero: number) {
    if (this.empenhoNumero > 0) {
      this.liquidacaoService.obterTotalLiquidadoPorEmpenho(numero, this.login.exercicio.id, this.login.orgao.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          (res: any) => {
            this.liquidado = res.liquidado;
            if (!this.entidade.id) {
              this.entidadeForm.get('valor_pago').setValue(this.liquidado);
              this.entidadeForm.get('valor_retido').setValue(res.retido);
              this.entidadeForm.get('valor_liquido').setValue(+res.liquidado - +res.retido);
            } else {
              this.entidadeForm.get('valor_liquido').setValue(+this.entidadeForm.get('valor_pago').value - +this.entidadeForm.get('valor_retido').value);
            }
            this.buscarTotalPago(numero);
          }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error })
        );
    }
  }

  public buscarTotalPago(numero: number) {
    this.pagamentoService.obterTotalPagoPorEmpenho(numero, this.login.exercicio.id, this.login.orgao.id).pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (res: any) => {
          this.saldoPagar = +this.liquidado - +res.pago;
        }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error })
      );
  }

  public voltar() {
    if (this.login.sistema === 'contabil') {
      this.router.navigate(['/tesouraria']);
    } else {
      this.sair();
    }

  }
}
